import { PrivateRoutes } from '@/constants/routes'
import { UserPool } from '@/constants/UserPool'
import { IConfigSeller } from '@/models/configSeller'
import { webApiService } from '@/services'
import { useAppDispatch } from '@/store/store'
import { setUser } from '@/store/user'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js'
import { enqueueSnackbar } from 'notistack'
import { FormEvent, useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import { MdAutorenew, MdVisibility, MdVisibilityOff } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ModalForgotPassword from './ModalForgotPassword/ModalForgotPassword'
import ModalResetPassword from './ModalResetPassword/ModalResetPassword'

// Login SSO (SAML) de WOM: redirect completo de la pestaña. El ACS vuelve a
// /:slug/backoffice/#session=<base64url>, que consume consumeSsoSession() en main.tsx.
const WOM_SSO_URL = 'https://jzyvkobsbh.execute-api.us-west-2.amazonaws.com/dev/saml/login'

export const LoginForm = () => {
  const [data, setData] = useState<{ username: string, password: string, loading: boolean, newPass: string }>({ username: '', password: '', loading: false, newPass: '' })
  const [showPass, setShowPass] = useState(false)
  const dispatch = useAppDispatch()
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const isWom = configSeller.config.s3.FOLDER_APP === 'ti_wom'
  const [modal, setModal] = useState<{ visible: boolean, name: string, parametros: any }>({ visible: false, name: '', parametros: {} })
  const [forgotModal, setForgotModal] = useState<{ visible: boolean, name: string, parametros: any }>({ visible: false, name: '', parametros: {} })

  const loginWom = (): void => {
    window.location.href = WOM_SSO_URL
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setData({ ...data, loading: true })

    const user = new CognitoUser({
      Username: data.username,
      Pool: UserPool
    })
    const authDetails = new AuthenticationDetails({
      Username: data.username,
      Password: data.password
    })

    user.authenticateUser(authDetails, {
      onSuccess: (response) => {
        const _poolId = import.meta.env.VITE_COGNITO_POOL_ID ?? ''
        webApiService.getUserProfile(response.getIdToken().payload.email as string, _poolId as string).then((res) => {
          if (res.success === true) {
            res.data.token = response.getIdToken().getJwtToken()
            res.data.logged = true
            dispatch(setUser(res.data))
            if (res.data.id_role === Number(import.meta.env.VITE_PERFIL_TECHNICIANS)) {
              navigate(PrivateRoutes.LISTADO_TECNICOS.replace(':slug', String(slug)))
            } else {
              navigate(PrivateRoutes.HOME.replace(':slug', String(slug)))
            }
          } else {
            enqueueSnackbar('getUserProfile RES ERROR : ' + res.message, { variant: 'error' })
          }
        }).catch((error: any) => {
          console.log('getProfile error:', error)
          enqueueSnackbar('Hubo un error con token vacío, revisar usuario en base de datos ...', { variant: 'error' })
        }).finally(() => {
          setData({ ...data, loading: false })
        })
      },
      onFailure: (err) => {
        setData({ ...data, loading: false })
        err.message === 'callback.newPasswordRequired is not a function' ? setModal({ ...modal, visible: true, parametros: { oldPass: data.password, currentUser: user, currentUserName: data.username, setNewPass, modalName: 'temporary' } }) : console.log(err)
        if (err.message === 'Incorrect username or password.') {
          enqueueSnackbar('Usuario o contraseña incorrectos, por favor, intente nuevamente', { variant: 'error' })
        } else {
          enqueueSnackbar('authenticateUser onFailure IF : ' + err.message, { variant: 'error' })
        }
        setData({ ...data, loading: false })
      },
      newPasswordRequired (userAttributes, _requiredAttributes) {
        setModal({
          ...modal,
          visible: true,
          parametros: {
            oldPass: data.password,
            currentUser: user,
            currentUserName: data.username,
            cnp: (newPass: string, confirmPassword: string) => {
              if (newPass === '' || confirmPassword === '') {
                enqueueSnackbar('No puedes dejar campos vacios', { variant: 'error' })
                return
              }
              if (newPass !== confirmPassword) {
                enqueueSnackbar('Las contraseñas no coinciden, por favor, intente nuevamente', { variant: 'error' })
                return
              }
              delete userAttributes.email_verified
              delete userAttributes.email
              user.completeNewPasswordChallenge(confirmPassword, userAttributes, {
                onSuccess: (_result) => {
                  setModal({ ...modal, visible: false, parametros: { isSuccess: 'success' } })
                  enqueueSnackbar('Contraseña guardada exitosamente, por favor, ingrese con su nueva contraseña', { variant: 'success' })
                  setData({ ...data, loading: false, username: '', password: '' })
                },
                onFailure: (err) => {
                  console.log('ON FAILURE ERROR LOGIN FORM: ', err)
                  setModal({ ...modal, visible: true, parametros: { isSuccess: 'error' } })
                }
              })
            }
          }
        })
        if (modal.visible) {
          setData({ ...data, loading: true })
        } else {
          setData({ ...data, loading: false })
        }
      }
    })
  }

  const setNewPass = (_newPass: string): void => {
    setData({ ...data, newPass: _newPass })
  }

  const forgotPass = (): void => {
    setForgotModal({ ...forgotModal, visible: true })
  }

  return (
    <>
      {modal.visible ? <ModalResetPassword modal={modal} setModal={setModal} /> : null}
      {forgotModal.visible ? <ModalForgotPassword modal={forgotModal} setModal={setForgotModal} /> : null}

      <form className='flex flex-col gap-4' onSubmit={handleSubmit}>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs uppercase tracking-widest text-slate-500'>Email</label>
          <Input
            type='email'
            autoComplete='email'
            value={data.username}
            onChange={(e) => setData({ ...data, username: e.target.value })}
            placeholder='tu@reuse.cl'
            className='h-11'
          />
        </div>

        <div className='flex flex-col gap-1.5'>
          <label className='text-xs uppercase tracking-widest text-slate-500'>Contraseña</label>
          <div className='relative'>
            <Input
              type={showPass ? 'text' : 'password'}
              autoComplete='current-password'
              value={data.password}
              onChange={(e) => setData({ ...data, password: e.target.value })}
              placeholder='••••••••'
              className='h-11 pr-10'
            />
            <Button
              type='button'
              variant='ghost'
              size='icon-sm'
              onClick={() => setShowPass(v => !v)}
              className='absolute right-1 top-1/2 -translate-y-1/2 text-slate-400'
            >
              {showPass ? <MdVisibilityOff /> : <MdVisibility />}
            </Button>
          </div>
        </div>

        <div className='flex justify-end'>
          <Button type='button' variant='link' size='sm' onClick={forgotPass} className='h-auto p-0 text-xs'>
            ¿Olvidaste tu contraseña?
          </Button>
        </div>

        <Button
          type='submit'
          size='lg'
          disabled={data.loading || data.password.trim() === '' || data.username.trim() === ''}
          className='mt-1 w-full'
        >
          {data.loading ? <MdAutorenew className='animate-spin' /> : 'Ingresar'}
        </Button>
      </form>

      {isWom && (
        <div className='mt-4 flex flex-col gap-4'>
          <div className='flex items-center gap-3'>
            <div className='h-px flex-1 bg-slate-200' />
            <span className='text-xs text-slate-500'>o</span>
            <div className='h-px flex-1 bg-slate-200' />
          </div>
          <Button
            type='button'
            variant='outline'
            size='lg'
            onClick={loginWom}
            className='w-full'
          >
            <FcGoogle size={20} />
            Logéate con WOM
          </Button>
        </div>
      )}
    </>
  )
}
